import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, LayoutDashboard, Images, Info, Library, Newspaper, PlayCircle, Film, BarChart3, Trophy, Sparkles } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { isFeatureEnabled } from '../constants';

/**
 * NavMenu - Navigation menu items for the app shell drawer
 * @param {Object} props
 * @param {Function} props.onNavigate - Callback when navigation occurs (for closing mobile drawer)
 */
export default function NavMenu({ onNavigate }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  const handleNavigate = (path) => {
    navigate(path);
    onNavigate?.();
  };

  // `except` lets a broad prefix (e.g. /decks) yield to a nested sibling route
  // (/decks/tournament) so only one item ever reads as current.
  const isActive = (path, except = []) => {
    if (except.some((p) => location.pathname === p || location.pathname.startsWith(p + '/'))) {
      return false;
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const navItems = [
    {
      section: t('navigation.main') || 'Main',
      items: [
        {
          icon: Home,
          label: t('navigation.home') || 'Home',
          path: '/',
          exactMatch: true,
        },
      ],
    },
    {
      section: t('navigation.tools') || 'Tools',
      items: [
        {
          icon: LayoutDashboard,
          label: t('navigation.deckBuilder') || 'Deck Builder',
          path: '/deck-builder',
        },
        {
          icon: Library,
          label: t('navigation.sampleDecks') || 'Sample Decks',
          path: '/decks',
          except: ['/decks/tournament'],
        },
        {
          icon: Images,
          label: t('navigation.cardGallery') || 'Card Gallery',
          path: '/gallery',
        },
        ...(isFeatureEnabled('SET_REVEALS') ? [{
          icon: Sparkles,
          label: t('navigation.setReveals'),
          path: '/sets',
        }] : []),
        // Tournament meta analytics + deck database - behind feature flag
        ...(isFeatureEnabled('META_ANALYTICS') ? [{
          icon: BarChart3,
          label: t('navigation.metaAnalytics') || 'Meta Analytics',
          path: '/meta',
        }, {
          icon: Trophy,
          label: t('navigation.deckDatabase') || 'Deck Database',
          path: '/decks/tournament',
        }] : []),
        // Theater Mode - behind feature flag
        ...(isFeatureEnabled('ANIMATIONS_TOOL') ? [{
          icon: Film,
          label: t('navigation.theaterMode') || 'Theater Mode',
          path: '/animations',
        }] : []),
      ],
    },
    {
      section: t('navigation.info') || 'Info',
      items: [
        {
          icon: Newspaper,
          label: t('navigation.blog') || 'Blog',
          path: '/blog',
        },
        {
          icon: PlayCircle,
          label: t('navigation.videos') || 'Videos',
          path: '/videos',
        },
        {
          icon: Info,
          label: t('navigation.about') || 'About',
          path: '/about',
        },
      ],
    },
  ];

  return (
    <nav className="flex flex-col gap-1 p-2">
      {navItems.map((section, sectionIndex) => (
        <div key={section.section}>
          {sectionIndex > 0 && <Separator className="my-2" />}
          <h4 className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {section.section}
          </h4>
          {section.items.map((item) => {
            const Icon = item.icon;
            const active = item.exactMatch
              ? location.pathname === item.path
              : isActive(item.path, item.except);
            return (
              <Button
                key={item.path}
                variant={active ? 'secondary' : 'ghost'}
                className={cn(
                  'w-full justify-start gap-2 mb-1',
                  active && 'bg-primary/10 text-primary hover:bg-primary/20'
                )}
                onClick={() => handleNavigate(item.path)}
                aria-current={active ? 'page' : undefined}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </Button>
            );
          })}
        </div>
      ))}
    </nav>
  );
}
